/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 * window.addEventListener('on:breakpoint-change', (event) => {
 *  if (theme.mediaMatches.md) {
 *   console.log('we are not on mobile');
 *  }
 * });
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */


document.addEventListener('on:variant:change', (event) => {
   var buybuttonNonActive = document.querySelector('button.btn.btn--large.add-to-cart.notactive');
   if(buybuttonNonActive){
    buybuttonNonActive.classList.remove('notactive');
   }
}); 

      
function togglemakethisactive(el) {
  const wrapper = el.closest('.sizes__wrapper');
  if (!wrapper) return;

  // remove active from all children inside this wrapper
  wrapper.querySelectorAll('.active').forEach(btn => {
    btn.classList.remove('active');
  });

  // add active to the clicked one
  el.classList.add('active');

 
  // now search for the adjacent add-to-cart button
  const addToCartBtn = wrapper.querySelector('.btn.btn--large.add-to-cart');
  if (addToCartBtn) {
    addToCartBtn.classList.add('active');
    const newText = addToCartBtn.getAttribute('data-atctext');
    if (newText) {
      addToCartBtn.textContent = newText;
    }

    addToCartBtn.click();
  }

}



function addtocart(button){
 
// find adjacent .sizes__btns
  const sizesBtns = button.parentElement.querySelector('.sizes__btns');
  if (!sizesBtns) {
    console.warn("⚠️ No .sizes__btns found near the button.");
    return;
  }

  // find active item
  const activeItem = sizesBtns.querySelector('.sizes__wrapper--item.active');
  if (!activeItem) { 
    return;
  }

  // get data-id as integer
  const variantId = parseInt(activeItem.getAttribute('data-id'));

  if (isNaN(variantId)) {
    console.error("⚠️ Invalid or missing data-id on active size item.");
    return;
  }

button.classList.add("is-loading"); 
  
    
     fetch('/cart/add.js', {
        method: 'POST',
        headers: {
        'Content-Type': 'application/json',
        },
        body: JSON.stringify({
        id: variantId,
        quantity: 1
        })
    })
    .then(response => response.json())
    .then(data => {
        console.log('Success:', data);
        // Update cart UI here
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
            bubbles: true
        }));
        document.dispatchEvent(new CustomEvent('on:cart:change'));
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
        button.classList.remove("is-loading");
    })
    .catch((error) => {
        console.error('Error:', error);
    });
 } 






  Object.defineProperty(HTMLMediaElement.prototype, 'playing', {
    get: function () {return !!(this.currentTime > 0 && !this.paused && !this.ended && this.readyState > 2);}
  });
  document.body.addEventListener("click", playVideoOnLowPower);
  document.body.addEventListener("touchstart", playVideoOnLowPower, { passive: true });
  function playVideoOnLowPower(){
    try{ const videoElements = document.querySelectorAll("video.custom_video:not([data-csd-lazy])");
    for (let i = 0; i < videoElements.length; i++) {
    if (videoElements[i].playing) {
     //console.log('Playing');
    } else {
      videoElements[i].play();
     //console.log('Not Playing');
    }}}
    catch(err) {console.log(err);}
  }
/* CSD-MENU-CARRITO-27OCT · Daniel, 27-oct-2026 (Galaxy Z Fold 8): «el carrito, cuando lo abres, después del menú, se queda
   detrás». En las pantallas táctiles anchas el menú grande (SHOP…) se abre con un toque y no se cerraba con nada: al tocar la
   bolsa, el carrito se abría por detrás del menú. Ahora el menú se cierra al abrirse el carrito y al tocar fuera de la barra
   de menús. Con ratón no cambia nada (el menú ya se cierra al sacar el ratón). */
(function () {
  try {
    var cierraMenu = function () {
      document.querySelectorAll('.navigation__item--show-children').forEach(function (el) {
        el.dispatchEvent(new Event('mouseleave'));
      });
    };
    /* el aviso del carrito no sube por la página: se escucha en la fase de captura */
    document.addEventListener('on:cart-drawer:before-open', cierraMenu, true);
    document.addEventListener('touchstart', function (e) {
      if (!document.querySelector('.navigation__item--show-children')) return;
      if (e.target && e.target.closest && e.target.closest('.navigation__tier-1')) return;
      cierraMenu();
    }, { passive: true, capture: true });
  } catch (e) {}
})();
/* /CSD-MENU-CARRITO-27OCT */

/* CSD-MEDIDAS-27OCT · Daniel, 27-oct-2026, maqueta 01 «Columnas» de «Medidas en la descripción» («vamos con esta hazlo
   live»): las medidas de la descripción salen en columnas (rótulo pequeño arriba, medida debajo, línea fina entre columnas),
   como las tallas S / M / L de las pulseras. Las tallas seguidas ya las pone en columnas snippets/csd-desc-pro.liquid; esto
   añade, sobre esa misma versión (la <template class="csd-dp-plantilla"> y su copia .csd-bal-pro), los párrafos que son
   SOLO una medida y que el snippet deja como texto:
     «50cm adjustable length» / «Comes with a 55cm adjustable chain» → LENGTH 50 cm · FIT Adjustable
     «Comes with a 50cm + 5cm adjustable chain» / «17 + 3cm adjustable length» → MIN 50 cm · MAX 55 cm (28-oct, Daniel: solo mínimo y máximo)
     «35cm length» / «64 CM» / «21 cm» → LENGTH 35 cm
     «Adjustable at 33cm, 40cm, 44cm and 50cm» (con o sin «50cm.» delante) → MIN 33 cm · MAX 50 cm (los del medio no, Daniel 28-oct)
     «3mm chain» → CHAIN WIDTH 3 mm
     tallas sueltas que el snippet no junta («S: 18cm» + «M/L: 21cm», o una sola) → S 18 cm · M/L 21 cm
   Un párrafo con algo más (p. ej. «… Pre-order. This item will be shipped…») se queda como está. En /es/ los rótulos van en
   español. Las columnas usan las mismas clases que las del snippet (csd-dp-tallas, csd-dp-t), así que su letra, sus
   medidas y sus colores son los mismos. Copia de antes en ronda13/medidas-27oct/copia-antes/. */
(function () {
  try {
    var es = /^es/i.test(document.documentElement.lang || '');
    var T = es
      ? { len: 'Largo', fit: 'Ajuste', adj: 'Ajustable', chain: 'Cadena', ext: 'Extensor', max: 'Máx.', min: 'Mín.', at: 'Ajustable', grosor: 'Grosor' }
      : { len: 'Length', fit: 'Fit', adj: 'Adjustable', chain: 'Chain', ext: 'Extender', max: 'Max', min: 'Min', at: 'Adjustable', grosor: 'Chain width' };
    var N = '(\\d+(?:[.,]\\d+)?)';
    var R = {
      ext: new RegExp('^(?:comes with an? )?' + N + '\\s*(?:cm)?\\s*\\+\\s*' + N + '\\s*cm(?:\\s+(?:fully\\s+)?adjustable)?(?:\\s+(chain|length))?$', 'i'),
      adj: new RegExp('^(?:comes with an? )?' + N + '\\s*cm\\s+(?:fully\\s+)?adjustable(?:\\s+(?:length|chain))?$', 'i'),
      uno: new RegExp('^' + N + '\\s*cm(?:\\s+(?:length|long|chain))?$', 'i'),
      at: new RegExp('^(?:' + N + '\\s*cm\\.\\s*)?adjustable at\\s+(.+)$', 'i'),
      mm: new RegExp('^' + N + '\\s*mm\\s+chain$', 'i'),
      talla: new RegExp('^(XXS|XS|S|M|L|XL|XXL|XXXL|2XL|3XL|M/L|S/M|L/XL)\\s*:?\\s*' + N + '\\s*(cm|mm)\\b([^0-9]*)$', 'i')
    };
    var esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
    var suma = function (a, b) {
      var n = parseFloat(a.replace(',', '.')) + parseFloat(b.replace(',', '.'));
      var t = String(Math.round(n * 10) / 10);
      return (a.indexOf(',') > -1 || b.indexOf(',') > -1) ? t.replace('.', ',') : t;
    };
    /* devuelve [{dt, dd, nota}] o null; talla: true si es una línea de talla (se juntan las seguidas) */
    var lee = function (t) {
      var m;
      if ((m = t.match(R.ext))) return { c: [{ dt: T.min, dd: m[1] + ' cm' }, { dt: T.max, dd: suma(m[1], m[2]) + ' cm' }] };
      if ((m = t.match(R.adj))) return { c: [{ dt: T.len, dd: m[1] + ' cm' }, { dt: T.fit, dd: T.adj }] };
      if ((m = t.match(R.uno))) return { c: [{ dt: T.len, dd: m[1] + ' cm' }] };
      if ((m = t.match(R.mm))) return { c: [{ dt: T.grosor, dd: m[1] + ' mm' }] };
      if ((m = t.match(R.at))) {
        var v = m[2].split(/\s*,\s*|\s+and\s+|\s+y\s+/), c = [];
        for (var i = 0; i < v.length; i++) {
          var x = v[i].match(/^(\d+(?:[.,]\d+)?)\s*cm$/i);
          if (!x) return null;
          c.push(x[1]);
        }
        return c.length > 1 ? { c: [{ dt: T.min, dd: c[0] + ' cm' }, { dt: T.max, dd: c[c.length - 1] + ' cm' }] } : null;
      }
      if ((m = t.match(R.talla))) {
        var nota = m[4].replace(/[()]/g, ' ').replace(/\b(length|long|largo)\b/ig, ' ').replace(/[.,]/g, ' ').replace(/\s+/g, ' ').trim();
        nota = nota ? nota.charAt(0).toUpperCase() + nota.slice(1) : '';
        return { talla: true, c: [{ dt: m[1].toUpperCase(), dd: m[2] + ' ' + m[3].toLowerCase(), nota: nota }] };
      }
      return null;
    };
    var fila = function (celdas) {
      var cols = Math.max(3, celdas.length), h = '<dl class="csd-dp-tallas" data-csd-medidas style="--csd-dp-cols: ' + cols + '">';
      celdas.forEach(function (c, i) {
        h += '<div class="csd-dp-t' + (i % cols === 0 ? ' csd-dp-t--ini' : '') + '"><dt>' + esc(c.dt) + '</dt><dd class="csd-dp-t__medida">' + esc(c.dd) + '</dd>' +
          (c.nota ? '<dd class="csd-dp-t__nota">' + esc(c.nota) + '</dd>' : '') + '</div>';
      });
      return h + '</dl>';
    };
    var procesa = function (dp) {
      if (!dp || dp.getAttribute('data-csd-medidas')) return;
      dp.setAttribute('data-csd-medidas', '1');
      var ps = [].slice.call(dp.children).filter(function (n) { return n.tagName === 'P' && !n.hasAttribute('data-csd-dp'); });
      var grupo = [];
      var cierra = function () {
        if (!grupo.length) return;
        var celdas = [];
        grupo.forEach(function (g) { celdas = celdas.concat(g.r.c); });
        grupo[0].p.insertAdjacentHTML('beforebegin', fila(celdas));
        grupo.forEach(function (g) { g.p.remove(); });
        grupo = [];
      };
      ps.forEach(function (p) {
        var t = (p.textContent || '').replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim().replace(/\.$/, '').trim();
        var r = /\d/.test(t) ? lee(t) : null;
        var pegado = grupo.length && grupo[grupo.length - 1].p.nextElementSibling === p;
        if (r && r.talla && pegado && grupo[0].r.talla) { grupo.push({ p: p, r: r }); return; }
        cierra();
        if (r && r.talla) { grupo.push({ p: p, r: r }); return; }
        if (r) { grupo.push({ p: p, r: r }); cierra(); }
      });
      cierra();
      /* la misma aclaración en todas las tallas («Adjustable length» en XS, M y XL del Mako): una sola vez, debajo */
      [].slice.call(dp.querySelectorAll('dl.csd-dp-tallas')).forEach(function (dl) {
        var ts = [].slice.call(dl.querySelectorAll('.csd-dp-t'));
        if (ts.length < 2) return;
        var comun = null, igual = true;
        ts.forEach(function (t) {
          var n = [].slice.call(t.querySelectorAll('dd.csd-dp-t__nota:not(.csd-dp-t__nota--fuerte)')).map(function (d) { return d.textContent.trim(); });
          if (n.length !== 1) { igual = false; return; }
          if (comun === null) comun = n[0]; else if (comun !== n[0]) igual = false;
        });
        if (!igual || !comun) return;
        ts.forEach(function (t) { var d = t.querySelector('dd.csd-dp-t__nota:not(.csd-dp-t__nota--fuerte)'); if (d) d.remove(); });
        dl.insertAdjacentHTML('afterend', '<p>' + esc(comun) + '.</p>');
      });
    };
    var todo = function () {
      document.querySelectorAll('template.csd-dp-plantilla').forEach(function (t) { if (t.content) procesa(t.content.querySelector('.csd-dp')); });
      document.querySelectorAll('.csd-bal-pro .csd-dp, .cj-tab .csd-dp').forEach(procesa);
    };
    todo();
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { setTimeout(todo, 0); });
    window.addEventListener('load', todo);
  } catch (e) {}
})();
/* /CSD-MEDIDAS-27OCT */

/* CSD-METAL-AJAX-28OCT · Daniel, 28-oct-2026: «aquí en este menú donde Silver y Gold [...] cuando pinchas en Silver, no
   debería de recargar la página entera y llevarte arriba. Simplemente debería de recargar la parte de abajo, que es el rol
   de productos». Los botones SILVER / GOLD de la página de colección (.csdmet--pagina, sections/main-collection.liquid) son
   enlaces a /collections/<x>/silver y /gold. Aquí el clic no cambia de página: pide solo la sección de la colección
   (?section_id=…, como la paginación del tema), cambia la rejilla, la barra de arriba y la paginación ([data-ajax-container]),
   los dos botones y la cifra del título, cambia la dirección del navegador y no mueve la página. Con atrás / adelante del
   navegador hace lo mismo. Si algo falla, va a la dirección como antes. Copia de antes: medidas-27oct/copia-antes/assets/. */
(function () {
  try {
    if (!window.fetch || !window.history || !history.pushState) return;
    var carga = function (url, empuja) {
      var fc = document.querySelector('filter-container[data-filter-section-id]');
      if (!fc) { location.href = url; return; }
      var sec = fc.closest('.shopify-section') || document;
      var conts = sec.querySelectorAll('[data-ajax-container]');
      conts.forEach(function (el) { el.classList.add('ajax-loading'); });
      if (empuja) history.pushState({ path: url, csdMetal: 1 }, '', url);
      fetch(url + (url.indexOf('?') >= 0 ? '&' : '?') + 'section_id=' + encodeURIComponent(fc.getAttribute('data-filter-section-id')))
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (h) {
          var t = document.createElement('template'); t.innerHTML = h;
          var nuevos = t.content.querySelectorAll('[data-ajax-container]');
          if (nuevos.length !== conts.length) { location.href = url; return; }
          nuevos.forEach(function (n, i) { conts[i].innerHTML = n.innerHTML; conts[i].classList.remove('ajax-loading'); });
          ['.csdmet--pagina', '.csd-pt__n'].forEach(function (s) {
            var a = sec.querySelector(s), z = t.content.querySelector(s);
            if (a && z) a.outerHTML = z.outerHTML;
          });
          if (typeof fc.initFiltersEtc === 'function') fc.initFiltersEtc();
          var de = document.querySelector('.utility-bar:not(.utility-bar--sticky-mobile-copy) .utility-bar__centre');
          var a2 = document.querySelector('.utility-bar--sticky-mobile-copy .utility-bar__centre');
          if (de && a2) a2.innerHTML = de.innerHTML;
        })
        .catch(function () { location.href = url; });
    };
    document.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('.csdmet--pagina a.csdmet__b');
      if (!b || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      e.preventDefault();
      /* el botón cambia al momento, sin esperar a la rejilla */
      var caja = b.closest('.csdmet--pagina'), on = !b.classList.contains('is-on');
      caja.querySelectorAll('.csdmet__b').forEach(function (x) { x.classList.remove('is-on'); x.setAttribute('aria-pressed', 'false'); });
      if (on) { b.classList.add('is-on'); b.setAttribute('aria-pressed', 'true'); }
      carga(b.getAttribute('href'), true);
    }, true);
    window.addEventListener('popstate', function () {
      if (document.querySelector('.csdmet--pagina')) carga(location.pathname + location.search, false);
    });
  } catch (e) {}
})();
/* /CSD-METAL-AJAX-28OCT */
